import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Agent } from '../agent'
import type * as common from '../common'

import type { Access } from './access'
import type { App } from './app'
import type { Context as _Context } from './context'
import type { Scenario } from './scenario'
import type { Tenant } from './tenant'
import type { Zone } from './zone'

// NOTE: this makes creating a TypeChecker easier
type UserBase = common.Identified<User.Id> & common.Named & common.Tagged
/**
 * An end user of Attain Apps
 */
export interface User extends UserBase {
  /**
   * ID of the user in Cognito
   */
  cognitoId?: string
  /**
   * Marks this user as a Platform Admin user.
   *
   * Platform Admin users can access the Platform Admin Portal and manage
   * platform-level users and entities.
   */
  isPlatformAdmin?: boolean
  /**
   * Marks this user as tenant-scoped rather than platform-level.
   *
   * Tenant users belong to the tenant they are created for and are hidden from
   * the platform-level Users page.
   */
  isTenant?: boolean
  firstName?: string
  lastName?: string
  employeeId?: string
  jobTitle?: string
  email?: string
  phoneNumber?: string
}
export namespace User {
  /**
   * Identifies a {@link User}
   */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> =
      TypeChecker.isString.usingAlias('User.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Provides access level for a given User context
   */
  export interface InContext extends User {
    tenantAccess?: Access
    zoneAccess?: Access
    appAccess?: Access
    scenarioAccess?: Access
    agents?: readonly AgentAccess[]
  }

  /**
   * Agent access information for a user
   */
  export interface AgentAccess {
    agentId: Agent.Id
    access: Access
    pageContext?: {
      pageAliases?: readonly string[]
      isForNonPrebuiltPages?: boolean
    }
  }
  /**
   * Provides User context information
   */
  export type Context = _Context.OneOf<'tenant' | 'zone' | 'app' | 'scenario'>

  /**
   * Information about a {@link User} that can be shown to other users
   */
  export type ShareableInfo = Pick<User, 'id' | 'name'>

  /**
   * Information needed to create a {@link User}
   *
   * NOTE: Omitting 'name' from input as the User object already contains
   * 'firstName' and 'lastName'; 'name' will be derived from these properties.
   */
  export type Input = Required.Semi<
    Omit<User, 'id' | 'cognitoId' | 'name'>,
    'email'
  >

  /**
   * Information used to update a {@link User}
   */
  export type Updates = Partial<Omit<Input, 'isTenant'>>

  /**
   * {@link User} with a list {@link Tenant.ForUser} that this {@link User} has
   * access to
   */
  export interface WithTenants extends User {
    /**
     * A list of {@link Tenant.ForUser} that this {@link User} has access to
     */
    tenants: readonly Tenant.ForUser[]
  }

  /**
   * {@link User} with additional information pertaining to the associated
   * {@link Tenant}
   */
  export interface InTenant extends User {
    /** {@link Access} that this user has to this {@link Tenant} */
    tenantAccess: Access
    /**
     * List of {@link Zone.ForUser} that this {@link User} has access to
     */
    zones: readonly Zone.ForUser[]
  }

  /**
   * {@link User} with additional information pertaining to the associated
   * {@link Zone}
   */
  export interface InZone extends User {
    /** {@link Access} that this user has to this {@link Zone} */
    zoneAccess: Access
    /**
     * List of {@link App.ForUser} that this {@link User} has access to
     */
    apps: readonly App.ForUser[]
    /**
     * List of {@link Agent.ForUser} that this {@link User} has access to
     */
    agents: readonly Agent.ForUser[]
  }

  /**
   * {@link User} with additional information pertaining to the associated
   * {@link App}
   */
  export interface InApp extends User {
    /** {@link Access} that this user has to this {@link App} */
    appAccess: Access
    /**
     * List of {@link Scenario.ForUser} that this {@link User} has access to
     */
    scenarios: readonly Scenario.ForUser[]
  }

  /**
   * {@link User} with additional information pertaining to the associated
   * {@link Scenario}
   */
  export interface InScenario extends User {
    /** {@link Access} that this user has to this {@link Scenario} */
    scenarioAccess: Access
  }
}
